import { useState } from 'react';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { Interstitial } from '@/features/onboarding/Interstitial';
import { PlanChoice } from '@/features/onboarding/PlanChoice';
import type { BillingPeriod } from '@/features/pricing/PlanCards';
import { startCheckout } from '@/lib/api';
import type { Tier } from '@/lib/tier';

/**
 * Pre-auth routes. These render OUTSIDE the app shell — no nav, no sidebar, because the user
 * is not authenticated yet.
 *
 * `plan` is read from the query string, which is how the marketing CTA carries the chosen plan
 * into the interstitial so it can name an exact charge amount. A generic "Get Started" arrives
 * without it and falls back to a range.
 */
function planFromQuery(): Tier | null {
  const value = new URLSearchParams(window.location.search).get('plan');
  return value === 'snapshot' || value === 'intelligence' || value === 'pro' ? value : null;
}

/** Today + 14 days, mm/dd/yyyy. The date is always exact, whatever plan is or isn't chosen. */
function trialChargeDate(): string {
  const d = new Date();
  d.setDate(d.getDate() + 14);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()}`;
}

export function InterstitialRoute() {
  const { login, register } = useKindeAuth();
  const [handedOff, setHandedOff] = useState(false);

  // Both buttons converge on Kinde's hosted screens; `register` and `login` differ only in
  // which tab opens first. The toast stays because the redirect is not instant and a button
  // that appears to do nothing gets pressed again.
  const handOff = (go: () => Promise<void>) => () => {
    setHandedOff(true);
    void go();
  };

  return (
    <>
      {handedOff && <p className="onboard__handoffToast">Taking you to sign in…</p>}
      <Interstitial
        plan={planFromQuery()}
        chargeDate={trialChargeDate()}
        onCreateAccount={handOff(register)}
        onLogIn={handOff(login)}
      />
    </>
  );
}

/**
 * Plan choice, post-auth.
 *
 * The only thing sent to the API is the tier and the billing period. The price is resolved
 * server-side from `price_map` and the account from the bearer token, so a tampered request
 * cannot name someone else's account or a price of its choosing — see `api/checkout.py`.
 */
export function PlanChoiceRoute() {
  const { user, isAuthenticated, isLoading } = useKindeAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const choose = (tier: Tier, period: BillingPeriod) => {
    setBusy(true);
    setError(null);
    startCheckout(tier, 'USD', period)
      .then(({ checkoutUrl }) => {
        if (!checkoutUrl) throw new Error('no checkout url');
        // Full navigation, not a new tab: a popup here is blocked often enough that the
        // customer sees nothing happen and assumes the plan did not take.
        window.location.assign(checkoutUrl);
      })
      .catch(() => {
        setBusy(false);
        setError('We could not open checkout. Please try again.');
      });
  };

  // Nothing renders until identity is known. Rendering the cards first would let someone
  // press one before there is a token to send, and the API would answer 401 — a confusing
  // failure for what is really just a race with sign-in.
  if (isLoading) return null;
  if (!isAuthenticated) {
    window.location.assign('/sign-in');
    return null;
  }

  return (
    <PlanChoice
      accountEmail={user?.email ?? ''}
      chargeDate={trialChargeDate()}
      onChoose={choose}
      busy={busy}
      error={error}
    />
  );
}
